import { router } from '@inertiajs/vue3';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const HISTORY_KEY = 'bayhive:navigation-scroll';
const position = (element) => ({ top: element.scrollTop, left: element.scrollLeft });
const tableScroller = (table) =>
  table?.querySelector('.p-virtualscroller') ||
  table?.querySelector('.p-datatable-table-container');

// Table content is loaded separately from the Inertia page. Native restoration
// happens before those rows exist, so retain the intended position until the
// destination has enough content to reach it.
export const useNavigationScroll = ({ content, page, enabled, navigationLoading }) => {
  const restoring = ref(false);
  let frozen = false;
  let historyVisit = false;
  let activeVisit = null;
  let departure = null;
  let nextDeparture = null;
  let saveFrame = null;
  let restoreFrame = null;
  let observer = null;
  let expiry = null;
  let pending = null;
  let generation = 0;
  let settledFrames = 0;
  const unsubscribe = [];
  const scrolledRegions = new Set();

  const identity = () => `${page.component}:${window.location.pathname}${window.location.search}`;
  const available = () => enabled.value && content.value;

  const locator = (element) => {
    const root = content.value;
    const table = element.closest('.p-datatable');
    if (table && tableScroller(table) === element) {
      return { table: [...root.querySelectorAll('.p-datatable')].indexOf(table) };
    }
    const path = [];
    for (let node = element; node && node !== root; node = node.parentElement) {
      path.unshift([...node.parentElement.children].indexOf(node));
    }
    return { path };
  };

  const resolve = (saved) => {
    if (saved.table !== undefined) {
      return tableScroller(content.value?.querySelectorAll('.p-datatable')[saved.table]);
    }
    return saved.path.reduce((node, index) => node?.children[index], content.value);
  };

  const snapshot = (discover = false) => {
    for (const element of scrolledRegions) {
      if (!content.value.contains(element)) scrolledRegions.delete(element);
    }
    return {
      identity: identity(),
      document: { top: window.scrollY, left: window.scrollX },
      // Include nested/mobile scroll areas as well as PrimeVue tables. Store only
      // coordinates and DOM locations, never table records or form contents.
      regions: (discover
        ? [content.value, ...content.value.querySelectorAll('*')]
        : [...scrolledRegions]
      )
        .filter(
          (element) => content.value.contains(element) && (element.scrollTop || element.scrollLeft),
        )
        .map((element) => ({ ...locator(element), ...position(element) })),
    };
  };

  const cancelSave = () => {
    if (saveFrame !== null) window.cancelAnimationFrame(saveFrame);
    saveFrame = null;
  };

  const save = () => {
    cancelSave();
    if (!available() || frozen || pending) return;
    router.remember(snapshot(), HISTORY_KEY);
  };

  const stopRestoring = () => {
    generation += 1;
    if (restoreFrame !== null) window.cancelAnimationFrame(restoreFrame);
    restoreFrame = null;
    observer?.disconnect();
    observer = null;
    window.clearTimeout(expiry);
    expiry = null;
    pending = null;
    settledFrames = 0;
    restoring.value = false;
  };

  const closeEnough = (actual, target) =>
    Math.abs(actual.top - target.top) <= 1 && Math.abs(actual.left - target.left) <= 1;

  const attemptRestore = () => {
    restoreFrame = null;
    if (!pending || !available()) return;
    // The shell keeps this page mounted but hidden during restoration. Its
    // combined aria-busy includes our own mask, so only the raw navigation
    // state and the page's data regions can gate readiness here.
    const loading =
      navigationLoading?.value ||
      [...content.value.querySelectorAll('[aria-busy="true"]')].some(
        (region) => !region.closest('.shop-management-shell-navigation-skeleton')
          && region.getClientRects().length,
      );
    if (loading) {
      settledFrames = 0;
      return;
    }
    const target = pending;
    let complete = true;
    for (const saved of target.regions) {
      const element = resolve(saved);
      if (!element || !element.getClientRects().length) {
        // A responsive view change or empty/error state may remove a region.
        // Once data is ready, a missing old region must not hide the page.
        continue;
      }
      const reachable = {
        top: Math.min(saved.top, Math.max(0, element.scrollHeight - element.clientHeight)),
        left: Math.min(saved.left, Math.max(0, element.scrollWidth - element.clientWidth)),
      };
      element.scrollTo({ ...reachable, behavior: 'instant' });
      complete = closeEnough(position(element), reachable) && complete;
    }
    const documentElement = document.scrollingElement;
    const reachableDocument = {
      top: Math.min(target.document.top, Math.max(0, documentElement.scrollHeight - documentElement.clientHeight)),
      left: Math.min(target.document.left, Math.max(0, documentElement.scrollWidth - documentElement.clientWidth)),
    };
    window.scrollTo({ ...reachableDocument, behavior: 'instant' });
    complete =
      closeEnough({ top: window.scrollY, left: window.scrollX }, reachableDocument) && complete;
    // Scroll events and row/transform mutations restart this settling period,
    // so virtual tables paint the restored range before the mask is removed.
    settledFrames = complete ? settledFrames + 1 : 0;
    if (settledFrames >= 2) stopRestoring();
    else if (complete) scheduleRestore();
  };

  const scheduleRestore = () => {
    if (pending && restoreFrame === null)
      restoreFrame = window.requestAnimationFrame(attemptRestore);
  };

  const restore = async (saved, { mask = false } = {}) => {
    stopRestoring();
    if (!saved || !available() || saved.identity !== identity()) return;
    pending = saved;
    restoring.value = mask;
    const currentGeneration = generation;
    await nextTick();
    if (currentGeneration !== generation || !available()) return;
    observer = new MutationObserver(() => {
      settledFrames = 0;
      scheduleRestore();
    });
    observer.observe(content.value, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-busy'],
    });
    scheduleRestore();
    // Failed requests or removed rows cannot keep pulling the user back.
    expiry = window.setTimeout(stopRestoring, 15000);
  };

  const onScroll = (event) => {
    if (!available()) return;
    if (event.target !== document && !content.value.contains(event.target)) return;
    if (event.target !== document) {
      if (event.target.scrollTop || event.target.scrollLeft) scrolledRegions.add(event.target);
      else scrolledRegions.delete(event.target);
    }
    if (pending) {
      settledFrames = 0;
      scheduleRestore();
      return;
    }
    if (frozen || saveFrame !== null) return;
    saveFrame = window.requestAnimationFrame(save);
  };

  const onBefore = (event) => {
    if (!available() || event.detail.visit.prefetch || event.detail.visit.async) return;
    stopRestoring();
    save();
    nextDeparture = snapshot(true);
    router.remember(nextDeparture, HISTORY_KEY);
  };

  const onStart = (event) => {
    if (!available() || event.detail.visit.prefetch || event.detail.visit.async) return;
    stopRestoring();
    historyVisit = false;
    activeVisit = event.detail.visit;
    departure = nextDeparture;
    nextDeparture = null;
    frozen = true;
    cancelSave();
  };

  const onPopstate = (event) => {
    if (!available() || !event.state?.page) return;
    stopRestoring();
    cancelSave();
    historyVisit = true;
    frozen = true;
    activeVisit = null;
    departure = null;
  };

  const onNavigate = () => {
    if (!available() || !historyVisit) return;
    historyVisit = false;
    frozen = false;
    void restore(router.restore(HISTORY_KEY), { mask: true });
  };

  const onFinish = (event) => {
    if (event.detail.visit !== activeVisit) return;
    activeVisit = null;
    frozen = false;
    // In-place visits keep the same document. Reapply its position if content
    // temporarily shrank while saving; ordinary new-page visits use Inertia's
    // top-of-page reset and hash handling.
    if (event.detail.visit.preserveScroll === true && departure?.identity === identity()) {
      void restore(departure);
    }
    departure = null;
  };

  const onInteraction = () => {
    stopRestoring();
    departure = null;
  };

  watch(() => navigationLoading?.value, () => {
    settledFrames = 0;
    scheduleRestore();
  });

  onMounted(() => {
    unsubscribe.push(
      router.on('before', onBefore),
      router.on('start', onStart),
      router.on('navigate', onNavigate),
      router.on('finish', onFinish),
    );
    window.addEventListener('popstate', onPopstate, true);
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(event, onInteraction, { capture: true, passive: true });
    }
    window.addEventListener('resize', scheduleRestore, { passive: true });
    void restore(router.restore(HISTORY_KEY), { mask: true });
  });

  onBeforeUnmount(() => {
    unsubscribe.forEach((remove) => remove());
    cancelSave();
    stopRestoring();
    scrolledRegions.clear();
    window.removeEventListener('popstate', onPopstate, true);
    document.removeEventListener('scroll', onScroll, true);
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.removeEventListener(event, onInteraction, true);
    }
    window.removeEventListener('resize', scheduleRestore);
  });

  return { restoring };
};
